import { Link } from 'react-router-dom';

import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import { css } from '@emotion/react';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type {
  CivilityId,
  FragmentType,
  GenericStatusId,
} from '@e-socna/data-layer';

import { PATH_PERSON_DEFAULT } from '../../app/constants/paths.constant';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useBuildPath } from '../../hooks/useBuildPath.hook';

import { GenericStatusIndicator } from '../GenericStatusIndicator';
import { Avatar } from '../Avatar';
import { thirdPartyTooltipOf } from '../../utils/third-party-tooltip.util';

export const PersonCardCmp_Person_Fragment = graphql(`
  fragment PersonCardCmp_Person_Fragment on Person {
    _id
    civilityId
    color
    fullName
    pictureImageUri
    statusId
  }
`);

interface Props {
  readonly person: FragmentType<typeof PersonCardCmp_Person_Fragment>;
}

export const PersonCard = ({ person: pPerson }: Readonly<Props>) => {
  const person = getFragmentData(PersonCardCmp_Person_Fragment, pPerson);

  const path = useBuildPath(PATH_PERSON_DEFAULT, { id: person._id });

  const { civilitiesIdMap } = useReferencesContext();

  const civilityLabel =
    civilitiesIdMap[person.civilityId as CivilityId]?.label ?? 'Inconnu';

  return (
    <Grid
      size={{
        lg: 6,
        xs: 12,
      }}
      sx={{ flexGrow: 1 }}
    >
      <Card>
        <CardHeader
          action={
            <GenericStatusIndicator
              statusId={person.statusId as GenericStatusId}
              variant="icon"
            />
          }
          avatar={
            <Avatar
              bgColor={person.color}
              fullName={person.fullName}
              imageUri={person.pictureImageUri}
              label={thirdPartyTooltipOf(person.fullName)}
            />
          }
          css={css`
            .MuiCardHeader-action {
              display: contents;
            }
          `}
        />
        <CardContent>
          <Typography
            component="div"
            gutterBottom
            sx={{ lineHeight: 1.2 }}
            variant="h6"
          >
            {person.fullName}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            {civilityLabel}
          </Typography>
        </CardContent>
        <CardActions>
          <Button component={Link} size="small" to={path} variant="outlined">
            Consulter
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );
};
